import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success("Welcome back.");
      nav(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-soma-bg flex">
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden border-r border-soma-border">
        <img src="https://images.unsplash.com/photo-1674504502895-3ac04ab2943e" alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-soma-bg via-soma-bg/50 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="font-serif-editorial italic text-3xl text-white leading-snug">"Focus is not force. It is the practiced ability to return."</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Activity className="w-5 h-5 text-soma-primary" strokeWidth={2.5} />
            <span className="font-display font-extrabold text-xl tracking-tight">neurofdback.ca</span>
          </Link>
          <h1 className="font-display font-semibold text-4xl mb-2">Welcome back</h1>
          <p className="text-soma-muted mb-10">Log in to continue your training.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-soma-muted">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-soma-surface border border-soma-border px-4 py-3 text-white outline-none focus:border-soma-primary transition-colors" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-soma-muted">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-soma-surface border border-soma-border px-4 py-3 text-white outline-none focus:border-soma-primary transition-colors" />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
            </button>
          </form>
          <p className="mt-8 text-soma-muted text-sm">No account? <Link to="/register" className="text-soma-primary hover:underline">Create one</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
